import type { ButtonHTMLAttributes, ElementType, MouseEventHandler, ReactNode, Ref } from 'react';
import type { To } from 'react-router-dom';
import { createContext, useContext, useMemo } from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { IconName } from '../../assets/Icon/Icon';
import type { TooltipProps } from '../../components/Tooltip/Tooltip';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { ControlSize } from '../shared/types';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantHeadingColor,
  variantRestBodyColor,
  variantRestIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { stateZIndexes } from '../../common/z_indexes';
import { DropdownMenu, DropdownMenuTrigger } from '../../components/DropdownMenu/DropdownMenu';
import { Tooltip, useInsideTooltipTrigger } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { ControlProvider, useControlSize } from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import {
  controlBorderRadius,
  controlHeight,
  controlIconSize,
  controlPaddingX,
  controlPaddingY,
  controlVars,
  getControlPreset,
} from '../shared/vars';

type SegmentsAppearance = 'box' | 'indicator';

type SegmentsContextValue = {
  appearance: SegmentsAppearance;
};

const SegmentsContext = createContext<SegmentsContextValue | undefined>(undefined);

type SegmentsProviderProps = {
  appearance?: SegmentsAppearance;
  children: ReactNode;
};

export function SegmentsProvider({ appearance = 'box', children }: SegmentsProviderProps) {
  const value = useMemo(() => ({ appearance }), [appearance]);
  return <SegmentsContext.Provider value={value}>{children}</SegmentsContext.Provider>;
}

const useSegmentsAppearance = () => useContext(SegmentsContext)?.appearance ?? 'box';

export const SegmentIcon = styled(Icon, {
  size: controlIconSize,
  color: '$$segmentIconColor',
});
SegmentIcon.displayName = 'SegmentIcon';

export const SegmentLabel = styled(Text, {
  color: '$$segmentLabelColor',
  fontWeight: '$bold',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
SegmentLabel.displayName = 'SegmentLabel';

// A menu segment's selection, shown beside the label the way a select shows its value.
export const SegmentValue = styled(Text, {
  color: '$$segmentIconColor',
  whiteSpace: 'nowrap',
});
SegmentValue.displayName = 'SegmentValue';

const SegmentContainer = styled('button', {
  position: 'relative',
  zIndex: stateZIndexes.base,
  display: 'flex',
  minWidth: 0,
  heightAll: controlHeight,
  paddingX: controlPaddingX,
  paddingY: controlPaddingY,
  borderRadius: controlBorderRadius,
  justifyContent: 'center',
  alignItems: 'center',
  transition: transitions.control,

  $$segmentLabelColor: variantHeadingColor,
  $$segmentIconColor: variantRestBodyColor,

  [selectors.hover]: {
    zIndex: stateZIndexes.hover,
  },

  [selectors.focus]: {
    zIndex: stateZIndexes.focus,
    outline: 'none',
    boxShadow: shadows.focusRingLight,
    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    selected: {
      true: {
        zIndex: stateZIndexes.focus,

        '&:after': {
          display: 'none',
        },
      },
      false: {
        zIndex: stateZIndexes.base,

        // An unselected segment reads as plain chrome whatever its variant, so it opts out of
        // the variant pickup above.
        $$segmentLabelColor: colors.headingNeutralLight,
        $$segmentIconColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          $$segmentLabelColor: colors.headingNeutralDark,
          $$segmentIconColor: colors.bodyNeutralDark,
        },
      },
    },
    appearance: {
      box: {
        borderRadius: '$8',
      },
      indicator: {
        '&::before': {
          content: '',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '$2',
          backgroundColor: 'transparent',
        },

        [selectors.focus]: {
          borderRadius: '$8',
        },
      },
    },
    arrangement: {
      'hidden-label': {},
      'leading-icon': {
        flexDirection: 'row',
      },
      'leading-label': {
        flexDirection: 'row-reverse',
      },
    },
    disabled: {
      true: {
        background: colors.bgNeutralLight,
        borderRadius: 0,
        opacity: 0.7,
        cursor: 'not-allowed',

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },

        '&:first-child': {
          borderTopLeftRadius: '$8',
          borderBottomLeftRadius: '$8',
        },

        '&:last-child': {
          borderTopRightRadius: '$8',
          borderBottomRightRadius: '$8',
        },
      },
      false: {},
    },
    variant: {
      destructive: { ...getVariantStyles('negative') },
      primary: { ...getVariantStyles('brand') },
    },
    ...controlVars,
  },

  compoundVariants: [
    {
      size: 'small',
      css: {
        minWidth: '$28',
      },
    },
    {
      size: 'medium',
      css: {
        minWidth: '$32',
      },
    },
    {
      size: 'large',
      css: {
        minWidth: '$40',
      },
    },
    {
      size: 'x-large',
      css: {
        minWidth: '$44',
      },
    },
    {
      appearance: 'indicator',
      size: 'small',
      css: {
        padding: '$4 0',
      },
    },
    {
      appearance: 'indicator',
      size: 'medium',
      css: {
        padding: '$4 0',
      },
    },
    {
      appearance: 'indicator',
      size: 'large',
      css: {
        padding: '$8 0',
      },
    },
    {
      appearance: 'indicator',
      size: 'x-large',
      css: {
        padding: '$8 0',
      },
    },
    {
      selected: true,
      appearance: 'box',
      css: {
        background: variantBackgroundColor,
        strokeAll: variantStrokeColor,
      },
    },
    {
      selected: true,
      appearance: 'indicator',
      css: {
        // The indicator bar marks which segment is current, so it shouldn't restate itself on
        // hover — hence the rest colour.
        '&:before': {
          backgroundColor: variantRestIconColor,
        },
      },
    },
    // An unselected destructive segment stays neutral until pointed at, then previews the
    // consequence.
    {
      selected: false,
      variant: 'destructive',
      css: {
        [selectors.hover]: {
          $$segmentLabelColor: variantHeadingColor,
          $$segmentIconColor: variantRestBodyColor,
        },
      },
    },
  ],
});
SegmentContainer.displayName = 'SegmentContainer';

type SegmentArrangement = 'hidden-label' | 'leading-icon' | 'leading-label';
type SegmentSize = ControlSize;
export interface SegmentProps {
  /**
   * Show a feature badge (e.g. internal, alpha, beta) next to the segment content.
   * Renders a small badge indicating the access level of the feature.
   */
  accessLevel?: AccessLevelProp;
  badge?: ReactNode;
  /**
   * Layout of icon and label within the segment.
   * - `leading-icon`: Icon before label (default)
   * - `leading-label`: Label before icon
   * - `hidden-label`: Icon only; label appears in tooltip on hover
   */
  arrangement?: SegmentArrangement;
  /**
   * When true, the segment is non-interactive and visually muted.
   * Disables onClick, routing, and keyboard focus.
   */
  disabled?: boolean;
  /**
   * Icon to display. Use with arrangement to control placement relative to the label.
   */
  icon?: IconName;
  /**
   * Label to display. With `hidden-label` arrangement, the label is shown in the
   * tooltip instead of being rendered inline.
   */
  label: string;
  /**
   * A `DropdownMenuPopover` the segment opens instead of acting on its own click, for a
   * segment standing in for an open-ended set of options — pick the particular one from the
   * menu. The segment gains a dropdown arrow; drive `label` and `selected` from the menu's
   * selection.
   */
  menu?: ReactNode;
  /**
   * When true, the segment appears selected (highlighted). Use with onClick for
   * controlled selection state in a segment group.
   */
  selected?: boolean;
  /**
   * The current selection, shown beside the label the way a select shows its value. Pair with
   * `menu` and drive it from the menu's state.
   */
  value?: string;
  /**
   * Visual size: `x-small`, `small`, `medium`, `large`, or `x-large`.
   * Defaults to `medium` when omitted. Inherits from `Segments` when provided there.
   */
  size?: SegmentSize;
  /**
   * Tooltip props to wrap the segment. When using `hidden-label`, the label
   * becomes the tooltip content unless you pass custom `contents`.
   */
  tooltip?: TooltipProps;
  /**
   * Visual style: `primary` (default) or `destructive`.
   * Use `destructive` for dangerous or irreversible actions.
   */
  variant?: 'destructive' | 'primary';
  /**
   * Routing
   */
  as?: ElementType;
  to?: To;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  ref?: Ref<HTMLButtonElement>;
}

export function Segment({
  accessLevel,
  arrangement = 'leading-icon',
  badge,
  disabled = false,
  icon,
  label,
  menu,
  selected = false,
  size,
  tooltip,
  value,
  variant = 'primary',
  // Routing
  as,
  to,
  onClick,
  ref,
  ...remaining
}: SegmentProps) {
  const controlSize = useControlSize(size, 'medium');
  const appearance = useSegmentsAppearance();
  const insideTooltipTrigger = useInsideTooltipTrigger();
  const controlPreset = getControlPreset(controlSize);

  const renderSegment = (
    triggerProps?: ButtonHTMLAttributes<HTMLButtonElement> & {
      ref?: Ref<HTMLButtonElement>;
    },
  ) => (
    <SegmentContainer
      type="button"
      tabIndex={disabled ? -1 : undefined}
      role="tab"
      as={disabled ? undefined : as}
      to={disabled ? undefined : to}
      onClick={disabled ? undefined : onClick}
      aria-selected={selected ? 'true' : 'false'}
      selected={selected}
      appearance={appearance}
      arrangement={arrangement}
      size={controlSize}
      variant={variant}
      disabled={disabled}
      {...remaining}
      {...triggerProps}
      ref={mergeRefs(
        [ref, triggerProps?.ref].filter(
          (candidate): candidate is Ref<HTMLButtonElement> => candidate != null,
        ),
      )}
    >
      <AlignStack
        aria-label={arrangement === 'hidden-label' ? label : undefined}
        direction={arrangement === 'leading-label' ? 'row-reverse' : 'row'}
        gap={4}
        width="fit-content"
        preset={controlPreset}
        start={icon && <SegmentIcon icon={icon} />}
        end={
          badge ??
          (menu ? (
            <SegmentIcon icon="chevron-down" />
          ) : (
            accessLevel && <FeatureBadge type={accessLevel} size="x-small" />
          ))
        }
      >
        {arrangement !== 'hidden-label' && label && (
          <SegmentLabel>
            {label}
            {value != null && <SegmentValue> {value}</SegmentValue>}
          </SegmentLabel>
        )}
      </AlignStack>
    </SegmentContainer>
  );

  // The menu's trigger handlers replace the segment's own click; selecting happens in the menu.
  if (menu) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger disabled={disabled} render={(props) => renderSegment(props)} />
        {menu}
      </DropdownMenu>
    );
  }

  if (!insideTooltipTrigger && (tooltip || arrangement === 'hidden-label')) {
    return (
      <Tooltip {...tooltip} contents={tooltip?.contents ?? label}>
        {renderSegment()}
      </Tooltip>
    );
  }

  return renderSegment();
}

const SegmentsDivider = styled(Divider, {
  position: 'relative',
  zIndex: 0,

  variants: {
    size: {
      'x-small': {
        padding: '$4 0',
      },
      small: {
        padding: '$6 0',
      },
      medium: {
        padding: '$8 0',
      },
      large: {
        padding: '$10 0',
      },
      'x-large': {
        padding: '$12 0',
      },
    },
  },
});
SegmentsDivider.displayName = 'SegmentsDivider';

const SegmentsContainer = styled('div', {
  display: 'flex',
  width: '100%',

  variants: {
    appearance: {
      box: {
        borderRadius: '$8',
        background: colors.bgApplicationLight,
        strokeAll: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
          strokeAll: colors.strokeNeutralDark,
        },
      },
      indicator: {
        strokeBottom: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeBottom: colors.strokeNeutralDark,
        },
      },
    },
    contentMode: {
      fit: {
        width: 'fit-content',
      },
      stretch: {
        width: '100%',
      },
    },
    order: {
      'first-to-last': {
        flexDirection: 'row',
      },
      'last-to-first': {
        flexDirection: 'row-reverse',
      },
    },
    proportions: {
      equal: {
        [`& > ${SegmentContainer}`]: {
          flex: 1,
        },
      },
      fit: {},
    },
  },
});
SegmentsContainer.displayName = 'SegmentsContainer';

export type SegmentsOrder = 'first-to-last' | 'last-to-first';

export type SegmentsProps = {
  /**
   * Visual style of the segments container: `box` (filled background) or
   * `indicator` (underline on selected).
   */
  appearance?: SegmentsAppearance;
  /**
   * How content fills the container: `fit` (shrink to content) or
   * `stretch` (fill available space).
   */
  contentMode?: 'fit' | 'stretch';
  /**
   * Gap between segments if using `indicator` appearance.
   */
  gap?: SpacingScale;
  /**
   * Order of segments: `first-to-last` or `last-to-first`.
   */
  order?: SegmentsOrder;
  /**
   * How segments share space: `equal` (same width) or `fit` (shrink to content).
   */
  proportions?: 'equal' | 'fit';
  /**
   * Array of segment configs. Renders as `Segment` components with dividers.
   */
  segments: SegmentProps[];
  /**
   * Visual size: `x-small`, `small`, `medium`, `large`, or `x-large`.
   * Passed to child segments via context.
   */
  size?: SegmentSize;
};

export function Segments({
  appearance = 'box',
  contentMode = 'stretch',
  gap = 8,
  order = 'first-to-last',
  proportions = 'equal',
  segments,
  size = 'medium',
  ...remaining
}: SegmentsProps) {
  const segment = (props: SegmentProps, index: number) => (
    <Segment key={props.label ?? index} {...props} />
  );

  let content: ReactNode = null;
  if (segments?.length) {
    if (appearance === 'indicator') {
      content = segments.map((props, i) => segment(props, i));
    } else {
      content = segments.flatMap((props, index) =>
        index < segments.length - 1
          ? [
              segment(props, index),
              <SegmentsDivider
                key={`segments-divider-${props.label ?? index}`}
                appearance="thin"
                direction="column"
                size={size}
                variant="neutral"
              />,
            ]
          : [segment(props, index)],
      );
    }
  }

  const hasGap = appearance === 'indicator' && gap != null;
  const css = hasGap ? spacingCSS(gap) : undefined;

  return (
    <SegmentsContainer
      role="tablist"
      appearance={appearance}
      contentMode={contentMode}
      order={order}
      proportions={proportions}
      css={css}
      {...remaining}
    >
      <SegmentsProvider appearance={appearance}>
        <ControlProvider value={{ size }}>{content}</ControlProvider>
      </SegmentsProvider>
    </SegmentsContainer>
  );
}
